/**
 * SettingsPanel - Panel de configuración de la aplicación
 */

import { useUIStore, showSuccess } from '@/store';
import { cn } from '@/core/utils';
import { Button } from '@/ui/primitives';
import { useConfirm, ConfirmDialog } from '@/ui/composed';
import { ExportDataSection } from './ExportDataSection';
import { NotificationSettings } from './NotificationSettings';
import { SyncSection } from './SyncSection';
import {
  Moon,
  Sun,
  Volume2,
  VolumeX,
  Vibrate,
  Eye,
  EyeOff,
  RotateCcw,
  Sparkles,
  Palette,
  Type,
  Contrast,
} from 'lucide-react';

export function SettingsPanel() {
  const preferences = useUIStore((state) => state.preferences);
  const updatePreferences = useUIStore((state) => state.updatePreferences);
  const resetPreferences = useUIStore((state) => state.resetPreferences);

  const { requestConfirm: requestReset, dialogProps: resetDialogProps } = useConfirm({
    onConfirm: () => {
      resetPreferences();
      showSuccess('Preferencias restauradas');
    },
    title: '¿Restaurar preferencias?',
    description: 'Esto devolvera todas las configuraciones a sus valores originales.',
    confirmLabel: 'Restaurar',
    cancelLabel: 'Cancelar',
    variant: 'warning',
    reassurance: 'Puedes ajustar las preferencias de nuevo cuando quieras.',
  });

  return (
    <div className="space-y-6">
      {/* Apariencia */}
      <section>
        <h3 className="text-sm font-medium text-slate-300 mb-3">Apariencia</h3>
        <div className="space-y-3">
          {/* Tema */}
          <SettingRow
            label="Tema"
            description="Solo modo oscuro disponible por ahora"
          >
            <div className="flex gap-2">
              <Button
                variant={preferences.theme === 'dark' ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => updatePreferences({ theme: 'dark' })}
              >
                <Moon className="w-4 h-4" />
              </Button>
              <Button
                variant={preferences.theme === 'light' ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => updatePreferences({ theme: 'light' })}
                disabled
              >
                <Sun className="w-4 h-4" />
              </Button>
            </div>
          </SettingRow>

          {/* Modo compacto */}
          <SettingToggle
            label="Modo compacto"
            description="Reduce el espaciado para ver más contenido"
            enabled={preferences.compactMode}
            onChange={(enabled) => updatePreferences({ compactMode: enabled })}
          />

          {/* Mostrar fundamentos */}
          <SettingToggle
            label="Mostrar fundamentos"
            description="Muestra el 'por qué' científico de cada protocolo"
            enabled={preferences.showRationale}
            onChange={(enabled) => updatePreferences({ showRationale: enabled })}
            icon={preferences.showRationale ? Eye : EyeOff}
          />

          {/* Animaciones */}
          <SettingToggle
            label="Animaciones"
            description="Efectos visuales al completar tareas"
            enabled={preferences.animationsEnabled}
            onChange={(enabled) => updatePreferences({ animationsEnabled: enabled })}
            icon={Sparkles}
          />

          {/* Colores suaves */}
          <SettingToggle
            label="Colores suaves"
            description="Desatura colores para reducir fatiga visual"
            enabled={preferences.softColors}
            onChange={(enabled) => updatePreferences({ softColors: enabled })}
            icon={Palette}
          />

          {/* Alto contraste */}
          <SettingToggle
            label="Alto contraste"
            description="Maximiza legibilidad (WCAG AAA)"
            enabled={preferences.highContrast}
            onChange={(enabled) => updatePreferences({ highContrast: enabled })}
            icon={Contrast}
          />

          {/* Fuente accesible */}
          <SettingRow
            label="Tipografía"
            description="Fuente optimizada para accesibilidad"
          >
            <div className="flex gap-1">
              <Button
                variant={preferences.fontFamily === 'default' ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => updatePreferences({ fontFamily: 'default' })}
                title="Fuente estándar"
              >
                <Type className="w-4 h-4" />
              </Button>
              <Button
                variant={preferences.fontFamily === 'dyslexic' ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => updatePreferences({ fontFamily: 'dyslexic' })}
                title="OpenDyslexic - Mayor legibilidad"
                className="font-[OpenDyslexic]"
              >
                Aa
              </Button>
              <Button
                variant={preferences.fontFamily === 'mono' ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => updatePreferences({ fontFamily: 'mono' })}
                title="Monoespaciada - Caracteres diferenciados"
                className="font-mono"
              >
                <span className="text-xs">0O</span>
              </Button>
            </div>
          </SettingRow>
        </div>
      </section>

      {/* Feedback */}
      <section>
        <h3 className="text-sm font-medium text-slate-300 mb-3">Feedback</h3>
        <div className="space-y-3">
          {/* Sonidos */}
          <SettingToggle
            label="Sonidos"
            description="Reproduce sonidos al completar tareas"
            enabled={preferences.soundEnabled}
            onChange={(enabled) => updatePreferences({ soundEnabled: enabled })}
            icon={preferences.soundEnabled ? Volume2 : VolumeX}
          />

          {/* Vibración */}
          <SettingToggle
            label="Vibración"
            description="Feedback háptico en dispositivos móviles"
            enabled={preferences.hapticEnabled}
            onChange={(enabled) => updatePreferences({ hapticEnabled: enabled })}
            icon={Vibrate}
          />
        </div>
      </section>

      {/* Notificaciones */}
      <div className="pt-4 border-t border-slate-800">
        <NotificationSettings />
      </div>

      {/* Horarios */}
      <section>
        <h3 className="text-sm font-medium text-slate-300 mb-3">Horarios</h3>
        <div className="space-y-3">
          <SettingRow label="Hora de despertar">
            <input
              type="time"
              value={preferences.wakeTime}
              onChange={(e) => updatePreferences({ wakeTime: e.target.value })}
              className="exo-input w-full sm:w-32 text-center"
            />
          </SettingRow>

          <SettingRow label="Hora de dormir">
            <input
              type="time"
              value={preferences.sleepTime}
              onChange={(e) => updatePreferences({ sleepTime: e.target.value })}
              className="exo-input w-full sm:w-32 text-center"
            />
          </SettingRow>

          <SettingRow
            label="Reset automático"
            description="Hora en que se reinician las tareas diarias"
          >
            <input
              type="time"
              value={preferences.autoResetTime}
              onChange={(e) => updatePreferences({ autoResetTime: e.target.value })}
              className="exo-input w-full sm:w-32 text-center"
            />
          </SettingRow>
        </div>
      </section>

      {/* Exportacion de datos */}
      <div className="pt-4 border-t border-slate-800">
        <ExportDataSection />
      </div>

      {/* Sincronización */}
      <div className="pt-4 border-t border-slate-800">
        <SyncSection />
      </div>

      {/* Acciones */}
      <section className="pt-4 border-t border-slate-800">
        <Button
          variant="ghost"
          size="sm"
          onClick={requestReset}
          className="text-slate-400 hover:text-slate-200"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Restaurar valores por defecto
        </Button>
      </section>

      {/* Dialog de confirmacion */}
      <ConfirmDialog {...resetDialogProps} />

      {/* Info */}
      <section className="pt-4 border-t border-slate-800">
        <div className="text-center text-xs text-slate-600">
          <p>Exocortex v2.0</p>
          <p className="mt-1">Sistema Auxiliar Cognitivo</p>
          <p className="mt-2 text-slate-700">
            Diseñado con enfoque neurodiversidad-first
          </p>
        </div>
      </section>
    </div>
  );
}

// === Sub-componentes ===

interface SettingRowProps {
  label: string;
  description?: string;
  children: React.ReactNode;
}

function SettingRow({ label, description, children }: SettingRowProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4 p-3 rounded-lg bg-slate-800/30">
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-200">{label}</p>
        {description && (
          <p className="text-xs text-slate-500 mt-0.5">{description}</p>
        )}
      </div>
      <div className="flex-shrink-0">{children}</div>
    </div>
  );
}

interface SettingToggleProps {
  label: string;
  description?: string;
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  icon?: React.ElementType;
}

function SettingToggle({
  label,
  description,
  enabled,
  onChange,
  icon: Icon,
}: SettingToggleProps) {
  return (
    <button
      onClick={() => onChange(!enabled)}
      className={cn(
        'w-full flex items-center justify-between gap-4 p-3 rounded-lg',
        'transition-colors text-left',
        enabled ? 'bg-slate-800/50' : 'bg-slate-800/30 hover:bg-slate-800/40'
      )}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {Icon && (
          <Icon
            className={cn(
              'w-5 h-5 flex-shrink-0',
              enabled ? 'text-slate-200' : 'text-slate-500'
            )}
          />
        )}
        <div>
          <p className="text-sm text-slate-200">{label}</p>
          {description && (
            <p className="text-xs text-slate-500 mt-0.5">{description}</p>
          )}
        </div>
      </div>

      {/* Toggle switch */}
      <div
        className={cn(
          'relative w-11 h-6 rounded-full transition-colors flex-shrink-0',
          enabled ? 'bg-slate-400' : 'bg-slate-700'
        )}
      >
        <div
          className={cn(
            'absolute top-1 w-4 h-4 rounded-full bg-white transition-transform',
            enabled ? 'translate-x-6' : 'translate-x-1'
          )}
        />
      </div>
    </button>
  );
}
